import { getClient } from '../../web/realTime/realTime';
import { refreshCourseProgressData } from '../Courses/dispatchers';
import {
  updateBlockAssessment,
  updateBlockAttachments,
} from '../Blocks/dispatchers';
import { currentUserIdSelector } from '../Auth/selectors';

/**
 * Update subscriptions
 */
const realTimeClient = getClient('blocks');

class EventRegistry {
  constructor() {
    // channel clients used by each instance of a BlockPageUI mount
    // i.e. side-effect state for storing real-time client objects
    // {
    //   [blockId]: {
    //     [eventName]: clientObject
    //   }
    // }
    this.blockEventClients = {};

    // Keeping track of which UI mounts have registered which events on which blocks
    // {
    //   [mountId]: new Set([[blockId, eventName], ...])
    // }
    this.mountBlockEvents = {};

    // Keeping track of which events are mounted where
    // {
    //   [blockId]: {
    //     [eventName]: new Set([mountId, ...])
    //   }
    // }
    this.blockEventMounts = {};
  }

  _removeClient(blockId, eventName) {
    // remove all references to this blockId/eventName
    // within blockEventClients
    if (
      this.blockEventClients[blockId] &&
      this.blockEventClients[blockId][eventName]
    ) {
      delete this.blockEventClients[blockId][eventName];
    }
    if (
      this.blockEventClients[blockId] &&
      Object.keys(this.blockEventClients[blockId]).length === 0
    ) {
      delete this.blockEventClients[blockId];
    }

    // remove all references to this blockId/eventName
    // within blockEventMounts
    if (
      this.blockEventMounts[blockId] &&
      this.blockEventMounts[blockId][eventName]
    ) {
      delete this.blockEventMounts[blockId][eventName];
    }
    if (
      this.blockEventMounts[blockId] &&
      Object.keys(this.blockEventMounts[blockId]).length === 0
    ) {
      delete this.blockEventMounts[blockId];
    }
  }

  hasClient(blockId, eventName) {
    return Boolean(
      this.blockEventClients[blockId] &&
        this.blockEventClients[blockId][eventName]
    );
  }

  registerClient(blockId, eventName, client) {
    if (!this.blockEventClients[blockId]) {
      this.blockEventClients[blockId] = {};
    }
    this.blockEventClients[blockId][eventName] = client;
  }

  registerMount(blockId, eventName, mountId) {
    if (!this.blockEventMounts[blockId]) {
      this.blockEventMounts[blockId] = {};
    }
    if (!this.blockEventMounts[blockId][eventName]) {
      this.blockEventMounts[blockId][eventName] = new Set([]);
    }
    this.blockEventMounts[blockId][eventName].add(mountId);

    if (!this.mountBlockEvents[mountId]) {
      this.mountBlockEvents[mountId] = new Set([]);
    }
    this.mountBlockEvents[mountId].add([blockId, eventName]);
  }

  unmount(mountId, callback) {
    if (!this.mountBlockEvents[mountId]) {
      return;
    }
    this.mountBlockEvents[mountId].forEach(([blockId, eventName]) => {
      if (this.blockEventMounts[blockId]) {
        const mounts = this.blockEventMounts[blockId][eventName];
        mounts.delete(mountId);
        if (mounts.size === 0) {
          // this was the last subscribed event (for any UI mount)
          callback(this.blockEventClients[blockId][eventName]); // callback for removing the client
          this._removeClient(blockId, eventName); // clean up
        }
      }
    });

    delete this.mountBlockEvents[mountId];
  }
}

const eventRegistry = new EventRegistry();

// This matches BlockResource LiveEvents Enum
const LiveEvents = {
  BLOCK_COMPLETION_UPDATED: 'completed_by',
  BLOCK_ATTACHMENT_UPDATED: 'attached_by',
};

const eventDispatchers = {
  [LiveEvents.BLOCK_COMPLETION_UPDATED]: (dispatch, blockId) => (
    assessmentData
  ) => {
    // Block completion is stored independently to page/course data
    // and the top pie chart depends on correct block data
    // so update the block with the latest assessment
    // (completion, intermediary progress, etc.) data.
    dispatch(updateBlockAssessment(blockId, assessmentData));

    // Page completion is stored independently
    // and no action currently exists to inject completed block info (tofix?)
    // so just refresh everything
    dispatch(refreshCourseProgressData);
  },
  [LiveEvents.BLOCK_ATTACHMENT_UPDATED]: (dispatch, blockId) => (
    attachments
  ) => {
    // Update the user's attachment data for this block
    dispatch(updateBlockAttachments(blockId, attachments));
  },
};

export const subscribeToBlock = (mountId, blockId, eventName) => (
  dispatch,
  getState
) => {
  // Ensure the right data format is there for keeping track of

  const state = getState();
  const currentUserId = currentUserIdSelector(state);
  const eventHandler = eventDispatchers[eventName];

  if (eventHandler && !eventRegistry.hasClient(blockId, eventName)) {
    // No client is subscribed, create it
    const client = realTimeClient.channel([blockId, eventName, currentUserId]);
    const dispatcher = eventHandler(dispatch, blockId);
    client.watch(dispatcher);
    client.subscribe();

    // store this client in the registry
    eventRegistry.registerClient(blockId, eventName, client);
  }
  // keep track that this event is now subscribed
  // for this UI mount
  eventRegistry.registerMount(blockId, eventName, mountId);
};

export const unsubscribeAllBlocks = (mountId) => {
  // unmount this UI, and destroy any clients which were not
  // used in any other UI mounts
  eventRegistry.unmount(mountId, (client) => client.destroy());
};
